import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const steps = [
  {
    num: '01',
    title: 'Discover',
    subline: 'We listen. We align.',
    desc: 'We understand your vision, priorities, and constraints, beyond just the brief.',
    image: '/portfolio/drive_hero_2.jpg',
    alt: 'Discovering your vision'
  },
  {
    num: '02',
    title: 'Conceptualize',
    subline: 'Ideas take shape.',
    desc: 'Direction, mood, and narrative come together into a clear creative approach.',
    image: '/portfolio/drive_conceptualize_9.jpg',
    alt: 'Conceptualizing the experience'
  },
  {
    num: '03',
    title: 'Design & Execute',
    subline: 'Vision, delivered.',
    desc: 'From planning to production, every detail is managed with precision and accountability.',
    image: '/portfolio/drive_process_execute.jpg',
    alt: 'Designing and executing'
  },
  {
    num: '04',
    title: 'Experience',
    subline: 'Effortless to you.',
    desc: 'You arrive to a fully realized experience, seamless, elevated, and unforgettable.',
    image: '/portfolio/drive_private_3.jpg',
    alt: 'The final experience'
  }
];

function ProcessStep({ step, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const isReversed = index % 2 !== 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: "easeOut" }}
      data-testid={`process-step-${index}`}
      className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} min-h-[60vh] md:min-h-[75vh]`}
    >
      {/* Image */}
      <div className="w-full md:w-3/5 relative overflow-hidden">
        <div className="relative w-full h-[45vh] md:h-full">
          <motion.img
            initial={{ scale: 1.08 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ duration: 1.4, ease: "easeOut" }}
            src={step.image}
            alt={step.alt}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/12" />
        </div>
      </div>

      {/* Text */}
      <div className="w-full md:w-2/5 flex flex-col justify-center px-8 md:px-14 lg:px-20 py-14 md:py-0 bg-[#EFEAE6]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.3 }}
        >
          <span className="font-serif text-lg text-[#D8B4A0]/70">{step.num}</span>
          <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight text-[#0A0A0A] leading-[1.1]">
            {step.title}
          </h3>
          <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[#8C8C8C]/60 font-light">
            {step.subline}
          </p>
          <div className="editorial-line mt-6 mb-6" />
          <p
            className="font-light text-[13px] leading-[1.9] text-[#0A0A0A]/55"
            style={{ letterSpacing: '0.2px' }}
          >
            {step.desc}
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function ProcessSection() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-80px" });

  return (
    <section
      id="process"
      data-testid="process-section"
      className="bg-[#EFEAE6]"
    >
      {/* Header */}
      <div ref={headerRef} className="py-16 md:py-28 px-6 md:px-16 lg:px-24">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="label-text" data-testid="process-label">How It Unfolds</span>
            <div className="editorial-line mt-4 mb-6" />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] leading-[1.05]">
              The Process
            </h2>

            {/* Section intro */}
            <div className="mt-8 md:mt-10 max-w-[600px]">
              <p className="font-serif text-base sm:text-lg italic text-[#0A0A0A]/60 leading-[1.6]">
                A process designed for clarity, not chaos.
              </p>
              <p
                className="mt-4 font-light text-[13px] leading-[1.9] text-[#0A0A0A]/45"
                style={{ letterSpacing: '0.3px' }}
              >
                From first conversation to final execution, every step is structured, intentional, and handled with precision, so you always know where things stand, without needing to manage it yourself.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Steps */}
      <div>
        {steps.map((step, i) => (
          <ProcessStep key={step.title} step={step} index={i} />
        ))}
      </div>

      {/* Trust line */}
      <div className="py-14 md:py-20 px-6 md:px-16 lg:px-24 text-center">
        <p className="font-serif text-sm sm:text-base italic text-[#0A0A0A]/40 tracking-wide">
          Every detail is considered, so you never have to second guess anything.
        </p>
      </div>

      {/* Micro Trust Strip */}
      <div className="pb-16 md:pb-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-[600px] mx-auto">
          <div className="border-t border-[#0A0A0A]/6 pt-10 md:pt-14 text-center space-y-3">
            <p className="text-[11px] tracking-[0.15em] text-[#8C8C8C]/55 font-light">
              10+ years of experience
            </p>
            <p className="text-[11px] tracking-[0.15em] text-[#8C8C8C]/45 font-light">
              India &middot; UAE &middot; UK &middot; Hong Kong &middot; Thailand
            </p>
            <p className="text-[11px] tracking-[0.15em] text-[#8C8C8C]/40 font-light">
              Working with clients globally
            </p>
          </div>
          <div className="mt-10 md:mt-14 border-t border-[#0A0A0A]/6" />
        </div>
      </div>
    </section>
  );
}
